import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useIntl from '../../hooks/useIntl';
import { useBlueMoonActiveProperties } from '../../hooks/useLeaseSigningProvider';
import messages from './messages';

type Property = { id: string, name: string };

export const useBlueMoonActivePropertiesNote = ({
  organizationId,
  properties,
  propertiesError = false,
  propertiesLoading = false,
}: {
  organizationId: ?string,
  properties: Array<Property>,
  propertiesError?: boolean,
  propertiesLoading?: boolean,
}) => {
  const { blueMoonLeaseSigning = false } = useFlags();
  const { propertyIds, isLoading, isError, isForbidden } =
    useBlueMoonActiveProperties({
      organizationId,
      enabled: Boolean(blueMoonLeaseSigning),
    });

  return {
    isVisible: Boolean(blueMoonLeaseSigning),
    isForbidden,
    isError: isError || propertiesError,
    isLoading: isLoading || propertiesLoading,
    hasActiveProperties: properties.some((property) =>
      propertyIds.includes(property.id),
    ),
  };
};

/**
 * The document form shows property names as plain labels, so the badge goes after the name. Each
 * list layout of the form reads the same label.
 */
export const useBlueMoonPropertyLabels = ({
  organizationId,
  properties,
}: {
  organizationId: ?string,
  properties: Array<Property>,
}): Array<Property> => {
  const { formatMessage } = useIntl();
  const { blueMoonLeaseSigning = false } = useFlags();
  const { propertyIds } = useBlueMoonActiveProperties({
    organizationId,
    enabled: Boolean(blueMoonLeaseSigning),
  });

  return useMemo(() => {
    if (!properties.some((property) => propertyIds.includes(property.id))) {
      return properties;
    }
    return properties.map((property) =>
      propertyIds.includes(property.id)
        ? {
            ...property,
            name: formatMessage(messages.propertyLabel, {
              name: property.name,
            }),
          }
        : property,
    );
  }, [properties, propertyIds, formatMessage]);
};
